import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Loader from "../../components/ui/Loader";
import { useToast } from "../../contexts/ToastContext";
import { useGetProfileQuery } from "../../store/api/authApi";
import {
	useCreateConversationMutation,
	useGetConversationByIdQuery,
	useGetConversationsQuery,
	useSendMessageMutation,
	type ConversationListItem,
	type CreateConversationRequest,
	type Message,
} from "../../store/api/conversationsApi";
import { useUploadImageMutation } from "../../store/api/imageUploadApi";
import BuyerContextPanel from "./components/BuyerContextPanel";
import ChatArea from "./components/ChatArea";
import ConversationList, {
	type Conversation,
} from "./components/ConversationList";
import { NewConversationModal } from "./components/NewConversationModal";

interface TransformedConversation extends Conversation {
	propertyImage: string | null;
}

interface TransformedMessage {
	id: string;
	conversationId: string;
	sender: "agent" | "buyer";
	senderName: string;
	content: string;
	timestamp: Date;
	type: "text" | "file";
	fileName?: string;
	fileSize?: number;
	read: boolean;
}

const ClientMessagingCenter = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const emailParam = searchParams.get("email");
	const conversationParam = searchParams.get("conversation");

	const [selectedConversationId, setSelectedConversationId] = useState<
		string | null
	>(conversationParam);
	const [isTyping, setIsTyping] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [showBuyerPanel, setShowBuyerPanel] = useState(false);
	const [showConversationList, setShowConversationList] = useState(true); // Mobile: show list by default
	const [showNewConversationModal, setShowNewConversationModal] =
		useState(false);
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const { error: showError, success: showSuccess } = useToast();

	// Get current user profile
	const { data: currentUser } = useGetProfileQuery();

	// Fetch conversations list
	const {
		data: conversationsList,
		isLoading: isLoadingConversations,
		error: conversationsError,
		refetch: refetchConversations,
	} = useGetConversationsQuery();

	// Store conversation messages for unread count and last message calculation
	const [conversationMessagesCache, setConversationMessagesCache] = useState<
		Record<string, Message[]>
	>({});

	// Fetch selected conversation with messages
	const {
		data: selectedConversationData,
		isLoading: isLoadingMessages,
		refetch: refetchMessages,
	} = useGetConversationByIdQuery(selectedConversationId || "", {
		skip: !selectedConversationId,
	});

	// Cache messages for unread count calculation
	useEffect(() => {
		if (selectedConversationData?.messages && selectedConversationId) {
			setConversationMessagesCache((prev) => ({
				...prev,
				[selectedConversationId]: selectedConversationData.messages || [],
			}));
		}
	}, [selectedConversationData, selectedConversationId]);

	// Handle conversation query parameter
	useEffect(() => {
		if (conversationParam && conversationsList) {
			const conversation = conversationsList.find(
				(conv) => conv.id === conversationParam,
			);
			if (conversation) {
				setSelectedConversationId(conversationParam);
				setShowConversationList(false); // Hide conversation list on mobile when conversation is selected
			}
		}
	}, [conversationParam, conversationsList]);

	// Mutations
	const [sendMessage, { isLoading: isSendingMessage }] =
		useSendMessageMutation();
	const [createConversation, { isLoading: isCreatingConversation }] =
		useCreateConversationMutation();
	const [uploadImage, { isLoading: isUploadingFile }] =
		useUploadImageMutation();

	// Transform conversations list to component format
	const transformedConversations = useMemo<TransformedConversation[]>(() => {
		if (!conversationsList) return [];

		return conversationsList.map((conv: ConversationListItem) => {
			// Get cached messages for this conversation
			const cachedMessages = conversationMessagesCache[conv.id] || [];

			// Calculate unread count (messages not read by current user)
			const unreadCount = cachedMessages.filter(
				(msg: Message) =>
					msg.sender !== currentUser?.id &&
					!msg.is_read?.includes(currentUser?.id || ""),
			).length;

			// Get last message content
			let lastMessage = "No messages yet";
			if (cachedMessages.length > 0) {
				const lastMsg = cachedMessages[cachedMessages.length - 1];
				lastMessage = lastMsg.content;

				// If it's a file message, extract the file name
				const fileLinkMatch = lastMessage.match(/\[File: ([^\]]+)\]\([^)]+\)/);
				if (fileLinkMatch) {
					lastMessage = `📎 ${fileLinkMatch[1]}`;
				}
			}

			// Truncate long messages
			const truncatedLastMessage =
				lastMessage.length > 50
					? lastMessage.substring(0, 50) + "..."
					: lastMessage;

			return {
				id: conv.id,
				buyer_name: conv.subject || "Unknown Buyer",
				propertyInterest: conv.subject || "Property Inquiry",
				lastMessage: truncatedLastMessage,
				lastMessageTime: conv.last_message_at
					? new Date(conv.last_message_at)
					: new Date(conv.created_at),
				unreadCount,
				status: "online" as const,
				avatar: null,
				propertyImage: null,
			};
		});
	}, [conversationsList, conversationMessagesCache, currentUser?.id]);

	// Transform messages to component format
	const transformedMessages = useMemo<TransformedMessage[]>(() => {
		if (!selectedConversationData?.messages) return [];

		return selectedConversationData.messages.map((msg: Message) => {
			const isCurrentUser = msg.sender === currentUser?.id;

			// Check if message contains a file link
			const fileLinkMatch = msg.content.match(/\[File: ([^\]]+)\]\(([^)]+)\)/);
			const isFileMessage = !!fileLinkMatch;

			return {
				id: msg.id,
				conversationId: msg.conversation,
				sender: isCurrentUser ? "agent" : "buyer",
				senderName:
					msg.sender_name ||
					(isCurrentUser
						? currentUser?.first_name + " " + currentUser?.last_name
						: "Buyer"),
				content: msg.content,
				timestamp: new Date(msg.created_at),
				type: isFileMessage ? ("file" as const) : ("text" as const),
				fileName: isFileMessage ? fileLinkMatch[1] : undefined,
				fileSize: undefined, // File size not available from message
				read: msg.is_read?.includes(currentUser?.id || "") || false,
			};
		});
	}, [selectedConversationData, currentUser]);

	// Get selected conversation for display
	const selectedConversation = useMemo<Conversation | null>(() => {
		if (!selectedConversationId) return null;
		const found = transformedConversations.find(
			(c) => c.id === selectedConversationId,
		);
		if (!found) return null;
		// Extract only Conversation properties, excluding propertyImage
		const { propertyImage, ...conversation } = found;
		return conversation;
	}, [selectedConversationId, transformedConversations]);

	const scrollToBottom = () => {
		messagesEndRef?.current?.scrollIntoView({ behavior: "smooth" });
	};

	useEffect(() => {
		scrollToBottom();
	}, [transformedMessages]);

	const handleSendMessage = async (messageData: {
		text?: string;
		file?: File;
	}) => {
		if (!selectedConversationId || (!messageData.text && !messageData.file)) {
			showError("Please enter a message or attach a file.");
			return;
		}

		try {
			let messageContent = messageData.text || "";

			// Handle file upload if file is provided
			if (messageData.file) {
				try {
					// Convert file to base64
					const base64File = await new Promise<string>((resolve, reject) => {
						const reader = new FileReader();
						reader.onload = () => {
							const result = reader.result as string;
							resolve(result);
						};
						reader.onerror = reject;
						reader.readAsDataURL(messageData.file!);
					});

					// Upload to Cloudinary
					const uploadResult = await uploadImage({
						images: [base64File],
						folder: "messages",
					}).unwrap();

					if (uploadResult.uploaded && uploadResult.uploaded.length > 0) {
						const fileUrl = uploadResult.uploaded[0].url;
						const fileName = messageData.file.name;

						// Append file link to message content
						if (messageContent) {
							messageContent += `\n\n[File: ${fileName}](${fileUrl})`;
						} else {
							messageContent = `[File: ${fileName}](${fileUrl})`;
						}
					} else {
						throw new Error("File upload failed");
					}
				} catch (fileError) {
					console.error("Error uploading file:", fileError);
					showError("Failed to upload file. Please try again.");
					return;
				}
			}

			// Send message with content (text + file URL if applicable)
			await sendMessage({
				conversationId: selectedConversationId,
				data: { content: messageContent },
			}).unwrap();

			// Refetch messages to get updated list
			await refetchMessages();
			await refetchConversations();
			showSuccess("Message sent successfully!");
		} catch (error: any) {
			console.error("Error sending message:", error);
			const errorMessage =
				error?.data?.error ||
				error?.data?.message ||
				error?.message ||
				"Failed to send message. Please try again.";
			showError(errorMessage);
		}
	};

	const handleConversationSelect = (conversation: Conversation) => {
		setSelectedConversationId(conversation.id);
		setShowBuyerPanel(false);
		setShowConversationList(false); // Hide conversation list on mobile when conversation is selected
	};

	const handleBackToConversations = () => {
		setSelectedConversationId(null);
		setShowConversationList(true);
		setShowBuyerPanel(false);
	};

	const handleStartNewConversation = () => {
		setShowNewConversationModal(true);
	};

	const handleCreateConversation = async (
		conversationData: CreateConversationRequest,
	) => {
		try {
			const newConversation = await createConversation(
				conversationData,
			).unwrap();

			setShowNewConversationModal(false);
			setSelectedConversationId(newConversation.id);
			await refetchConversations();
			showSuccess("Conversation created successfully!");
		} catch (error: any) {
			console.error("Error creating conversation:", error);
			const errorMessage =
				error?.data?.error ||
				error?.data?.message ||
				error?.message ||
				"Failed to create conversation. Please try again.";
			showError(errorMessage, "Error Creating Conversation");
		}
	};

	const filteredConversations = useMemo(() => {
		if (!searchQuery) return transformedConversations;
		const query = searchQuery.toLowerCase();
		return transformedConversations.filter(
			(conv) =>
				conv.buyer_name.toLowerCase().includes(query) ||
				conv.propertyInterest.toLowerCase().includes(query),
		);
	}, [transformedConversations, searchQuery]);

	return (
		<div className="w-full">
			<div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
				<div className="flex h-screen bg-background">
					<div className="flex-1 flex flex-col">
						<div className="px-4 sm:px-6 py-4 border-b border-border bg-card">
							<h1 className="text-2xl font-bold text-foreground">
								Client Messaging Center
							</h1>
							<p className="text-muted-foreground mt-1">
								Live chat and communication management
							</p>
						</div>

						<div className="flex-1 flex">
							{/* Conversation List Sidebar */}
							<div
								className={`${
									showConversationList ? "flex" : "hidden"
								} lg:flex w-full lg:w-80 border-r border-border bg-card flex-col`}
							>
								<ConversationList
									conversations={filteredConversations.map(
										({ propertyImage, ...conv }) => conv,
									)}
									selectedConversation={selectedConversation}
									onConversationSelect={handleConversationSelect}
									onStartNewConversation={handleStartNewConversation}
									searchQuery={searchQuery}
									onSearchChange={setSearchQuery}
								/>
							</div>

							{/* Main Chat Area */}
							<div
								className={`${
									!showConversationList ? "flex" : "hidden"
								} lg:flex flex-1`}
							>
								<div className={`flex-1 ${showBuyerPanel ? "mr-80" : ""}`}>
									{isLoadingMessages ? (
										<div className="flex-1 flex items-center justify-center bg-muted/20">
											<Loader size="lg" showText text="Loading messages..." />
										</div>
									) : selectedConversation ? (
										<ChatArea
											conversation={selectedConversation}
											messages={transformedMessages}
											onSendMessage={handleSendMessage}
											isTyping={isTyping || isSendingMessage || isUploadingFile}
											onShowBuyerPanel={() =>
												setShowBuyerPanel(!showBuyerPanel)
											}
											showBuyerPanel={showBuyerPanel}
											messagesEndRef={messagesEndRef}
											onBack={handleBackToConversations}
										/>
									) : (
										<div className="flex-1 flex items-center justify-center bg-muted/20">
											<div className="text-center">
												<div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
													<svg
														className="w-8 h-8 text-muted-foreground"
														fill="none"
														stroke="currentColor"
														viewBox="0 0 24 24"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth={2}
															d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.959 8.959 0 01-4.906-1.456L3 21l2.456-5.094A8.959 8.959 0 013 12c0-4.418 3.582-8 8-8s8 3.582 8 8z"
														/>
													</svg>
												</div>
												<h3 className="text-lg font-medium text-foreground mb-2">
													Select a conversation
												</h3>
												<p className="text-muted-foreground mb-4">
													Choose a conversation from the sidebar to start
													messaging
												</p>
												<button
													onClick={handleStartNewConversation}
													className="bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
												>
													Start New Conversation
												</button>
											</div>
										</div>
									)}
								</div>

								{/* Buyer Context Panel */}
								{showBuyerPanel && selectedConversation && (
									<BuyerContextPanel
										conversation={selectedConversation}
										onClose={() => setShowBuyerPanel(false)}
									/>
								)}
							</div>
						</div>
					</div>

					{/* New Conversation Modal */}
					<NewConversationModal
						isOpen={showNewConversationModal}
						onClose={() => setShowNewConversationModal(false)}
						onCreateConversation={handleCreateConversation}
						isCreating={isCreatingConversation}
						organizationId={currentUser?.organization || ""}
					/>
				</div>
			</div>
		</div>
	);
};

export default ClientMessagingCenter;
